import {
  FloatingFormItem,
  InputDecimal,
  InputText,
  LicensePlateFormField,
} from '@/components/_common';
import { DeliveryType } from '@/core/domain/invoice/invoice/Invoice.types';
import { useStates } from '@/core/domain/zipcode/States/States.hooks';
import {
  Button,
  Col,
  Divider,
  Dropdown,
  Form,
  FormInstance,
  Input,
  Menu,
  Row,
  Select,
  Space,
  Typography,
} from 'antd';
import { DownOutlined } from '@ant-design/icons';
import { useIntl } from 'react-intl';

import InvoiceTransportCreateModal from './InvoiceTransportCreateModal';
import { useDisclosure } from '@/hooks';
import { useState } from 'react';
import { InvoiceTransportRequest } from '@/core/domain/invoice/invoiceTransport/invoiceCarrier.types';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import Authorities from '@/types/Authority';
import { useInvoiceFormContext } from '../../InvoiceFormProvider';
import CarrierSelectField from '@/components/_common/CarrierSelectField';

interface Props {
  form: FormInstance<InvoiceTransportRequest>;
}

type TranslateOptions = 'label' | 'select' | 'button' | 'dropdown';

export default function InvoiceTransportForm({ form }: Props) {
  const { data: states, isFetching } = useStates();
  const [modalVisible, setModalVisible] = useDisclosure();
  const [carrierId, setCarrierId] = useState();

  const { disabledFields } = useInvoiceFormContext();

  const intl = useIntl().formatMessage;
  const label = (message: string, type: TranslateOptions = 'label') =>
    intl({ id: `invoiceTransport.${type}.${message}` });

  const handleModalOpen = (type: 'create' | 'edit') => {
    if (type === 'edit') {
      setCarrierId(form.getFieldValue('carrierId'));
      return setModalVisible.open();
    } else {
      setCarrierId(undefined);
      return setModalVisible.open();
    }
  };

  const { Title } = Typography;

  const freightTypeOptions = [
    {
      value: DeliveryType.EMITTER,
      label: label('sender', 'select'),
    },
    {
      value: DeliveryType.RECIPIENT,
      label: label('recipient', 'select'),
    },
    {
      value: DeliveryType.THIRD_PARTY,
      label: label('third', 'select'),
    },
    {
      value: DeliveryType.SENDER_OWN_TRANSPORT,
      label: label('senderTransport', 'select'),
    },
    {
      value: DeliveryType.RECIPIENT_OWN_TRANSPORT,
      label: label('recipientTransport', 'select'),
    },
    {
      value: DeliveryType.NO_SHIPPING,
      label: label('noTransport', 'select'),
    },
  ];

  const carrierDropdownMenu = [
    {
      key: 'create',
      label: (
        <Button
          disabled={
            !hasPermissions([Authorities.FISCAL_INVOICE_CREATE_CARRIER]) ||
            disabledFields
          }
          type="primary"
          onClick={() => handleModalOpen('create')}
          block
        >
          {label('create', 'button')}
        </Button>
      ),
    },
    {
      key: 'edit',
      label: (
        <Form.Item shouldUpdate noStyle>
          {({ getFieldValue }) => (
            <Button
              type="primary"
              onClick={() => handleModalOpen('edit')}
              disabled={
                !getFieldValue('carrierId') ||
                !hasPermissions([Authorities.FISCAL_INVOICE_UPDATE_CARRIER]) ||
                disabledFields
              }
              block
            >
              {label('edit', 'button')}
            </Button>
          )}
        </Form.Item>
      ),
    },
  ];

  return (
    <Form form={form} layout="vertical" name="invoiceTransport">
      <Title level={4}>{label('transport')}</Title>

      <Form.Item hidden name="id" />

      <Row gutter={[16, 16]} justify="end">
        <Col xs={24} sm={18} md={8} xl={9}>
          <FloatingFormItem
            name="carrierId"
            label={label('shippingCompany')}
            style={{ marginBottom: '0' }}
          >
            <CarrierSelectField disabled={disabledFields} allowClear />
          </FloatingFormItem>
        </Col>

        <Col sm={6} md={5} lg={4} xl={3}>
          <Dropdown
            overlay={<Menu items={carrierDropdownMenu} />}
            placement="bottomRight"
          >
            <Space>
              <Typography.Text>{label('carrier', 'dropdown')}</Typography.Text>

              <DownOutlined />
            </Space>
          </Dropdown>
        </Col>

        <Col xs={24} sm={24} md={11} lg={12}>
          <FloatingFormItem
            name="shippingMode"
            label={label('shippingType')}
            initialValue={DeliveryType.EMITTER}
          >
            <Select
              options={freightTypeOptions}
              disabled={disabledFields}
              allowClear
            />
          </FloatingFormItem>
        </Col>
      </Row>

      <Divider />

      <Title level={4}>{label('transportVehicle')}</Title>

      <Row gutter={16}>
        <Col xs={24} sm={12} md={6}>
          <LicensePlateFormField
            name="plate"
            label={intl({
              id: 'customers.domain.vehicle.licensePlate',
            })}
            disabledFields={disabledFields}
            required={false}
          />
        </Col>

        <Col xs={24} sm={12} md={6}>
          <FloatingFormItem name="state" label={label('uf')}>
            <Select
              options={states?.data.map((cur) => ({
                value: cur.code,
                label: cur.code,
              }))}
              loading={isFetching}
              disabled={disabledFields}
              allowClear
            />
          </FloatingFormItem>
        </Col>

        <Col xs={24} sm={24} md={12}>
          <FloatingFormItem name="rntc" label={label('rntc')}>
            <InputText disabled={disabledFields} />
          </FloatingFormItem>
        </Col>
      </Row>

      <Divider />

      <Title level={4}>{label('volumes')}</Title>

      <Row gutter={16}>
        <Col xs={24} sm={8} md={6}>
          <FloatingFormItem name="volumeQuantity" label={label('quantity')}>
            <InputDecimal fractions={0} disabled={disabledFields} />
          </FloatingFormItem>
        </Col>

        <Col xs={24} sm={16} md={18}>
          <FloatingFormItem name="volumeType" label={label('specie')}>
            <Input disabled={disabledFields} />
          </FloatingFormItem>
        </Col>

        <Col xs={24} sm={12} md={6}>
          <FloatingFormItem name="brand" label={label('brand')}>
            <Input disabled={disabledFields} />
          </FloatingFormItem>
        </Col>

        <Col xs={24} sm={12} md={6}>
          <FloatingFormItem name="number" label={label('numbering')}>
            <InputText onlyNumber disabled={disabledFields} />
          </FloatingFormItem>
        </Col>

        <Col xs={24} sm={12} md={6}>
          <FloatingFormItem name="grossWeight" label={label('grossWeight')}>
            <InputDecimal disabled={disabledFields} fractions={3} />
          </FloatingFormItem>
        </Col>

        <Col xs={24} sm={12} md={6}>
          <FloatingFormItem name="netWeight" label={label('netWeight')}>
            <InputDecimal disabled={disabledFields} fractions={3} />
          </FloatingFormItem>
        </Col>
      </Row>

      <InvoiceTransportCreateModal
        visible={modalVisible}
        toggleModal={setModalVisible.toggle}
        carrierId={carrierId}
        resetCarrierId={() => setCarrierId(undefined)}
        resetList={() => form.resetFields(['carrierId'])}
      />
    </Form>
  );
}
